import type { HubConnection } from '@microsoft/signalr'
import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr'
import { APIS, ENV, LOADERS, SIGNAL_R_MESSAGES } from '../config/constants'
import { useRoute } from 'vue-router'
import { useLoader } from 'src/hooks/useLoader'
import type { ISettlement } from 'src/interfaces/payment'
import { debounce } from 'lodash-es'
import type { IMessage } from 'src/interfaces/common'
import {
  useAggStore,
  useAttStore,
  usePerStore,
  useNotificationStore,
  useAppStore,
  useOrgStore,
} from 'src/stores'
import { useNotificationSettingsStore } from 'src/common/store'
import { useMedicationStore } from 'src/stores/medication.store'
import { ref, computed } from 'vue'
import {
  useClientMessagingStore,
  useClientContextStore,
} from 'src/client/stores'
import { useAuthStore } from 'src/common/store'
import { showCrossCenterNotification } from 'src/utils/notificationActions'
import { NotificationType } from 'src/interfaces/notificationPreferences'

type SystemResponse = {
  Event: string
  Sender: string
  Target: string
  Text: string
  TimeStamp: string
}
type AdminUnreadMessagesCount = Record<string, Record<string, number>>
type ParentPortalUnreadMessagesCount = Record<string, number>

export const useSignalR = () => {
  const route = useRoute()
  const authStore = useAuthStore()
  const appStore = useAppStore()
  const perStore = usePerStore()
  const aggStore = useAggStore()
  const attStore = useAttStore()
  const messagingStore = useClientMessagingStore()
  const clientContextStore = useClientContextStore()
  const orgStore = useOrgStore()
  const notificationSettingsStore = useNotificationSettingsStore()
  const medicationStore = useMedicationStore()

  const connections = ref<{
    orgConnection: HubConnection | undefined
    userConnection: HubConnection | undefined
  }>({
    orgConnection: undefined,
    userConnection: undefined,
  })

  // Computed properties for connection states
  const isOrgConnected = computed(
    () =>
      connections.value.orgConnection?.state === HubConnectionState.Connected,
  )
  const isUserConnected = computed(
    () =>
      connections.value.userConnection?.state === HubConnectionState.Connected,
  )
  const isOrgDisconnected = computed(
    () =>
      connections.value.orgConnection?.state ===
      HubConnectionState.Disconnected,
  )
  const isUserDisconnected = computed(
    () =>
      connections.value.userConnection?.state ===
      HubConnectionState.Disconnected,
  )
  const isOrgDisconnecting = computed(
    () =>
      connections.value.orgConnection?.state ===
      HubConnectionState.Disconnecting,
  )
  const isUserDisconnecting = computed(
    () =>
      connections.value.userConnection?.state ===
      HubConnectionState.Disconnecting,
  )

  const fetchPaymentGroupsTasks: number[] = []
  const isIntentionalDisconnect = ref(false)

  async function initializeConnections() {
    isIntentionalDisconnect.value = false
    await initializeOrgConnection()
    await initializeUserConnection()
  }

  async function initializeOrgConnection() {
    try {
      await stopOrgConnection()

      const { usePostLoader: getOrgConnectionInfo } = useLoader(
        LOADERS.signalR,
        { uri: APIS.negotiate },
      )
      const orgConnectionData = await getOrgConnectionInfo({})
      const { accessToken: orgAccessToken, url: orgUrl } =
        orgConnectionData || {}
      const orgOptions = { accessTokenFactory: () => orgAccessToken }

      const { fetchByPageLoader: familyPageLoader } = useLoader(LOADERS.agg, {
        uri: APIS.family,
      })
      const { fetchByPageLoader: staffPageLoader } = useLoader(LOADERS.agg, {
        uri: APIS.staff,
      })
      const { useFetchLoader: fetchProcessing } = useLoader(LOADERS.agg, {
        uri: APIS.processing,
      })
      const { useFetchLoader: fetchSettlement } = useLoader(LOADERS.agg, {
        uri: APIS.settlement,
      })
      const { useFetchLoader: fetchPaymentGroups } = useLoader(LOADERS.agg, {
        uri: APIS.groupedSettlement,
      })
      const { useFetchLoader: fetchAdminUnreadMessagesCount } =
        useLoader<AdminUnreadMessagesCount>(LOADERS.per, {
          uri: 'unreadMessagesCount',
        })
      const { useFetchLoader: fetchParentPortalUnreadMessagesCount } =
        useLoader<ParentPortalUnreadMessagesCount>(LOADERS.per, {
          uri: 'parentPortalUnreadMessagesCount',
        })
      const { useFetchLoader: fetchAttendances } = useLoader(
        LOADERS.attendance,
        {
          uri: APIS.attendance,
        },
      )

      const fetchPaymentGroupsDebounced = debounce(() => {
        void fetchPaymentGroups({
          params: {
            year: aggStore.paymentGroupsSelectedYear,
            month: aggStore.paymentGroupsSelectedMonth,
            status: route.query.status,
          },
        }).then((result) => {
          if (!result) return
          aggStore.setPaymentGroupsAndSelectedMonthAndYear({
            paymentGroups: result,
            month: aggStore.paymentGroupsSelectedMonth,
            year: aggStore.paymentGroupsSelectedYear,
          })
        })
      }, 1000)

      connections.value.orgConnection = new HubConnectionBuilder()
        .withUrl(orgUrl, orgOptions)
        .withAutomaticReconnect()
        .configureLogging(
          process.env.ENV === ENV.DEV ? LogLevel.Information : LogLevel.None,
        )
        .build()

      connections.value.orgConnection?.on('system', (res: SystemResponse) => {
        const { Text, Event } = res

        switch (Event) {
          case SIGNAL_R_MESSAGES.getAdminUnreadMessages: {
            const isClient = authStore.isClient
            if (isClient) return
            const centerId = Text.split('_')[1]
            const localCenterId = authStore.currentCenterId
            if (localCenterId === centerId) {
              void fetchAdminUnreadMessagesCount().then((result) => {
                if (result) perStore.setAdminUnreadMessages(result)
                else perStore.setAdminUnreadMessages(null)
              })
              if (perStore.messagingSelectedMemberId) {
                const { useFetchLoader } = useLoader<IMessage[]>(LOADERS.per, {
                  uri: `${APIS.members}/${perStore.messagingSelectedMemberId}/${APIS.messages}`,
                })
                const opts = {
                  params: {
                    updateActivity: false,
                  },
                }
                void useFetchLoader(opts).then((result) =>
                  perStore.setMessages(result ?? []),
                )
              }
            }
            break
          }
          case SIGNAL_R_MESSAGES.getParentPortalUnreadMessages: {
            if (!authStore.isClient) return

            const [orgId, centerId, memberId] = Text.split('_')
            if (
              clientContextStore.orgId === orgId &&
              clientContextStore.centerId === centerId &&
              clientContextStore.loggedInMember
            ) {
              if (clientContextStore.loggedInMember.id !== memberId) return
              void fetchParentPortalUnreadMessagesCount().then((result) => {
                messagingStore.setUnreadMessages(result ?? {})
              })
              if (clientContextStore.loggedInMember.id) {
                const opts = {
                  params: {
                    updateActivity: false,
                  },
                }
                const { useFetchLoader } = useLoader<IMessage[]>(LOADERS.per, {
                  uri: `${APIS.members}/${clientContextStore.loggedInMember.id}/${APIS.messages}`,
                })
                void useFetchLoader(opts).then((result) =>
                  messagingStore.setMessages(result ?? []),
                )
              }
            }
            break
          }
          case SIGNAL_R_MESSAGES.getAttendance: {
            if (route.path !== '/') break
            const centerId = Text.split('_')[1]
            const localCenterId = authStore.currentCenterId
            if (localCenterId === centerId) {
              void fetchAttendances().then((result) => {
                attStore.setAttendancesAndNodes(result)
              })
            }
            break
          }
          case SIGNAL_R_MESSAGES.getMedicationList: {
            const centerId = Text.split('_')[1]
            const localCenterId = authStore.currentCenterId
            if (localCenterId === centerId) {
              medicationStore.triggerMedicationListUpdate()
            }
            break
          }
        }
        switch (Text) {
          case SIGNAL_R_MESSAGES.updatedFamily: {
            const match = route.path.match(/\/families/g)
            if (match && match.length > 0 && !aggStore.isCreatingNewFamily) {
              void familyPageLoader({
                params: {
                  limit: aggStore.familyNextOffset || appStore.tablePerPage,
                  offset: 0,
                },
              }).then((result) =>
                aggStore.setFamiliesAndTotal({
                  result: result.records,
                  total: result.total,
                  nextOffset: result.nextOffset,
                  isReplace: true,
                }),
              )
            }
            aggStore.setIsCreatingNewFamily(false)
            break
          }
          case SIGNAL_R_MESSAGES.updatedStaff: {
            const match = route.path.match(/\/staff/g)
            if (match && match.length > 0) {
              void staffPageLoader({
                params: {
                  limit: aggStore.staffNextOffset || appStore.tablePerPage,
                  offset: 0,
                },
              }).then((result) => {
                aggStore.setStaffAndTotal({
                  result: result.records,
                  total: result.total,
                  nextOffset: result.nextOffset,
                  isReplace: true,
                })
              })
            }
            break
          }
          case SIGNAL_R_MESSAGES.updatedRegistration: {
            const tenent = route.params.tenent
            if (tenent) {
              void clientContextStore.fetchFamilyDashboard()
            }
            break
          }
          case SIGNAL_R_MESSAGES.updatedProcessing: {
            const { subTab } = route.query
            const match =
              route.path.match(/\/finances/g) || route.path.match(/\/families/g)

            if (!match || match.length === 0) return

            const matchFamily = route.path.match(/\/families/g)
            if (route.query.tab || (matchFamily && matchFamily.length > 0)) {
              if (
                route.query.tab === 'processing' ||
                (matchFamily && matchFamily.length > 0 && subTab === 'finance')
              ) {
                void fetchProcessing({
                  params: {
                    year: aggStore.processingSelectedYear,
                    month: aggStore.processingSelectedMonth,
                    ...(aggStore.processingSelectedRange && {
                      from: aggStore.processingSelectedRange.from,
                    }),
                    ...(aggStore.processingSelectedRange && {
                      to: aggStore.processingSelectedRange.to,
                    }),
                    ...(aggStore.processingFamilyId && {
                      familyId: aggStore.processingFamilyId,
                    }),
                  },
                }).then((result) => {
                  aggStore.setProcessingAndSelectedMonthAndYear({
                    processing: result,
                    month: aggStore.processingSelectedMonth,
                    year: aggStore.processingSelectedYear,
                    range: aggStore.processingSelectedRange,
                    familyId: aggStore.processingFamilyId,
                  })
                })
              }
              if (route.query.tab === 'settlement') {
                fetchPaymentGroupsTasks.push(1)
                if (
                  route.query.status === 'Deposited' ||
                  route.query.status === 'Settled'
                ) {
                  fetchPaymentGroupsDebounced()
                  return
                }

                void fetchSettlement({
                  params: {
                    year: aggStore.settlementSelectedYear,
                    month: aggStore.settlementSelectedMonth,
                    ...(aggStore.settlementSelectedRange && {
                      from: aggStore.settlementSelectedRange.from,
                    }),
                    ...(aggStore.settlementSelectedRange && {
                      to: aggStore.settlementSelectedRange.to,
                    }),
                    ...(aggStore.settlementSelectedTab && {
                      status: aggStore.settlementSelectedTab,
                    }),
                  },
                }).then((result) => {
                  if (!result?.value) return
                  result.forEach((settlement: ISettlement) => {
                    settlement.payments &&
                      (settlement.payments[0].actual =
                        settlement.payments[0].amount)
                  })
                  aggStore.setSettlementAndSelectedMonthAndYear({
                    settlement: result,
                    month: aggStore.settlementSelectedMonth,
                    year: aggStore.settlementSelectedYear,
                    range: aggStore.settlementSelectedRange,
                    tab: aggStore.settlementSelectedTab,
                  })
                })
              }
            }
          }
        }
      })
      await connections.value.orgConnection.start()
      connections.value.orgConnection.onclose(reconnectIfNeeded)
    } catch {
      await stopOrgConnection()
    }
  }

  async function initializeUserConnection() {
    try {
      await stopUserConnection()

      const { usePostLoader: getUserConnectionInfo } = useLoader(
        LOADERS.signalR,
        { uri: `${APIS.negotiate}/${APIS.user}` },
      )
      const userConnectionData = await getUserConnectionInfo({})
      const { accessToken: userAccessToken, url: userUrl } =
        userConnectionData || {}
      const userOptions = { accessTokenFactory: () => userAccessToken }

      connections.value.userConnection = new HubConnectionBuilder()
        .withUrl(userUrl, userOptions)
        .withAutomaticReconnect()
        .configureLogging(
          process.env.ENV === ENV.DEV ? LogLevel.Information : LogLevel.None,
        )
        .build()

      const email = authStore.user?.email
      const orgId = authStore.currentOrgId
      const centerId = authStore.currentCenterId
      const familyId = clientContextStore.family?.id
      const role = authStore.role
      const availableCenters = authStore.availableCenters || []

      const roleGroups =
        availableCenters.length > 0
          ? availableCenters.map((center) => `role:${orgId}:${center}:${role}`)
          : authStore.isOrgAdmin
            ? [`role:${orgId}::${role}`]
            : [`role:${orgId}:${centerId}:${role}`]

      const centerGroups =
        availableCenters.length > 0
          ? availableCenters.map((center) => `center:${center}`)
          : centerId
            ? [`center:${centerId}`]
            : []

      const userGroups = [
        `user:${email}`,
        ...roleGroups,
        ...centerGroups,
        ...(familyId ? [`family:${familyId}`] : []),
      ]

      await Promise.allSettled(
        userGroups.map(async (group) => {
          try {
            const { usePostLoader: joinGroup } = useLoader(LOADERS.signalR, {
              uri: `${APIS.groups}/${APIS.add}`,
            })
            await joinGroup({ groupName: group })
          } catch {
            // Silently handle group join failures
          }
        }),
      )

      connections.value.userConnection?.on(
        'notification',
        async (res: SystemResponse) => {
          if (authStore.isSuperAdmin) return

          const notificationStore = useNotificationStore()
          const { Text, Event } = res

          switch (Event) {
            case SIGNAL_R_MESSAGES.newNotification: {
              const parts = Text.split('_')
              const notificationType = parts[0] as NotificationType
              const notificationCenterId = parts[1]

              if (notificationType === NotificationType.Message) {
                break
              }

              const isTypeEnabled =
                notificationSettingsStore.isInAppEnabledForCenter(
                  notificationType,
                  notificationCenterId,
                )

              if (!isTypeEnabled) {
                break
              }

              const isSameCenter =
                !notificationCenterId ||
                notificationCenterId === authStore.currentCenterId

              if (isSameCenter) {
                await notificationStore.handleNotificationReceived()
              } else {
                const isCrossCenterEnabled =
                  notificationSettingsStore.isPushEnabledForCenter(
                    notificationCenterId,
                  )

                if (isCrossCenterEnabled) {
                  const center = orgStore.centers.find(
                    (c) => c.id === notificationCenterId,
                  )
                  const centerName = center?.name || 'another center'

                  showCrossCenterNotification(notificationCenterId, centerName)
                }
              }
              break
            }
            case SIGNAL_R_MESSAGES.clearedNotification: {
              const entityId = Text
              notificationStore.clearEntity(entityId)
              break
            }
          }
        },
      )
      await connections.value.userConnection.start()
      connections.value.userConnection.onclose(reconnectIfNeeded)
    } catch {
      await stopUserConnection()
    }
  }

  function reconnectIfNeeded() {
    if (isIntentionalDisconnect.value) {
      return
    }

    if (isOrgDisconnected.value) {
      void initializeOrgConnection()
    }
    if (isUserDisconnected.value) {
      void initializeUserConnection()
    }
    if (isOrgDisconnecting.value || isUserDisconnecting.value) {
      setTimeout(reconnectIfNeeded, 5000)
    }
  }

  async function stopConnections() {
    isIntentionalDisconnect.value = true
    await Promise.all([stopOrgConnection(), stopUserConnection()])
  }

  async function stopOrgConnection() {
    if (isOrgConnected.value) {
      void connections.value.orgConnection?.stop()
    }
  }

  async function stopUserConnection() {
    if (isUserConnected.value) {
      const userId = authStore.user?.id
      const orgId = authStore.currentOrgId
      const centerId = authStore.currentCenterId
      const familyId = clientContextStore.family?.id
      const role = authStore.user?.role
      const availableCenters = authStore.availableCenters || []

      if (userId) {
        const roleGroups =
          availableCenters.length > 0
            ? availableCenters.map(
                (center) => `role:${orgId}:${center}:${role}`,
              )
            : authStore.isOrgAdmin
              ? [`role:${orgId}::${role}`]
              : [`role:${orgId}:${centerId}:${role}`]

        const centerGroups =
          availableCenters.length > 0
            ? availableCenters.map((center) => `center:${center}`)
            : centerId
              ? [`center:${centerId}`]
              : []

        const userGroups = [
          `user:${userId}`,
          ...roleGroups,
          ...centerGroups,
          ...(familyId ? [`family:${familyId}`] : []),
        ]

        void Promise.allSettled(
          userGroups.map(async (group) => {
            try {
              const { useDeleteBulk: leaveGroup } = useLoader(LOADERS.signalR, {
                uri: `${APIS.groups}/${APIS.remove}`,
              })
              await leaveGroup({
                groupName: group,
                userId: userId,
              })
            } catch {
              // Silently handle cleanup failures
            }
          }),
        )
      }

      await connections.value.userConnection?.stop()
    }
  }

  return {
    connections,
    initializeConnections,
    reconnectIfNeeded,
    stopConnections,
  }
}
